import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { RouteDefinition } from "./types";

type RoutesContextValue = {
  routes: RouteDefinition[];
  groupedRoutes: Record<string, RouteDefinition[]>;
};

const RoutesContext = createContext<RoutesContextValue | null>(null);

type RoutesProviderProps = {
  routes: RouteDefinition[];
  children: ReactNode;
};

export const RoutesProvider = ({ routes, children }: RoutesProviderProps) => {
  const groupedRoutes = useMemo(() => {
    return routes.reduce(
      (acc, route) => {
        const section = route.section || "Other";
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(route);
        return acc;
      },
      {} as Record<string, RouteDefinition[]>,
    );
  }, [routes]);

  return <RoutesContext.Provider value={{ routes, groupedRoutes }}>{children}</RoutesContext.Provider>;
};

export const useRoutes = (): RoutesContextValue => {
  const context = useContext(RoutesContext);
  if (!context) {
    throw new Error("useRoutes must be used within a RoutesProvider");
  }
  return context;
};
